// English strings (source of truth). Keys are dot-namespaced. The Spanish
// dictionary (es.js) mirrors these keys; any missing key falls back to English,
// then to the key itself, so partial translation is always safe.
//
// IMPORTANT: values here must match the exact text the UK /staff portal already
// shows, because /staff renders in 'en' — changing an English value changes the
// live UK UI.

const en = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    search: 'Search…',
    viewAll: 'View all',
    loading: 'Loading…',
    newTour: 'New Tour',
    all: 'All',
    delete: 'Delete',
    view: 'View →',
    generating: 'Generating…',
  },
  dashboard: {
    greetingMorning: 'Good morning',
    greetingAfternoon: 'Good afternoon',
    greetingEvening: 'Good evening',
    headerSub: "Here's what needs your attention today.",
    actionRequired: 'Action required',
    reviewTours: 'Review tours →',
    chaseInvoices: 'Chase invoices →',
    viewAll: 'View all',
    // KPI cards
    awaitingReview: 'Awaiting Review',
    awaitingReviewSub: 'new requests',
    departures30d: 'Departures · 30d',
    departures30dSub: 'next 30 days',
    activeTours: 'Active Tours',
    activeToursSub: 'in operations',
    completedTours: 'Completed Tours',
    completedToursSub: 'post-tour checklist done',
    outstanding: 'Outstanding',
    // Card titles
    revenue: 'Revenue',
    tourPipeline: 'Tour Pipeline',
    pipelineSub: 'Live tours by lifecycle stage',
    toursInProgress: 'Tours in Progress',
    needsAttention: 'Needs Attention',
    invoicesDue: 'Invoices Due',
    outstandingSuffix: 'outstanding',
    nextDepartures: 'Next Departures',
    nextDeparturesSub: 'Countdown to wheels-up',
    noOutstanding: 'No outstanding invoices.',
    operationsWorkload: 'Operations Workload',
    totalLivePipeline: 'Total live pipeline',
    periodToday: 'Today', periodWeek: 'Week', periodMonth: 'Month',
    periodYear: 'Year', periodQuarter: 'Quarter',
  },
  category: {
    all: 'All',
    sales_admin: 'Sales & Admin',
    tour_preparation: 'Tour Preparation (Operations)',
    leader_handover: 'Operations to Tour Leader Handover',
    leader_signoff: 'Tour Leader Sign Off',
    post_tour: 'Post Tour',
    cancelled: 'Cancelled',
    archived: 'Archived',
  },
  tours: {
    title: 'Tours',
    countSuffix: 'in the system',
    inOperations: 'in operations',
    filterAll: 'All',
    filterCancelled: 'Cancelled',
    noneTitle: 'No tours in this category',
    noneDesc: 'Tours will appear here once they are created.',
    noMatchTitle: 'No tours match "{term}"',
    noMatchDesc: 'Try a different search term or clear the filter.',
    loadMore: 'Load more tours',
    searchPlaceholder: 'Search tours…',
  },
  invoices: {
    title: 'Invoices',
    subtitle: 'All invoices and instalments across every booking',
    viewByRequest: 'By Request', viewAllInvoices: 'All Invoices',
    filterAll: 'All', filterUnpaid: 'Unpaid', filterPaid: 'Paid',
    outstanding: 'Outstanding', received: 'Received', totalInvoices: 'Total Invoices',
    paidLower: 'paid', outstandingLower: 'outstanding',
    collectionRate: 'Collection rate', ofTotalCollected: 'of total invoiced value collected',
    noneFound: 'No invoices found.', allPaid: 'All paid', nUnpaid: '{n} unpaid',
    receivedTotal: 'Received / Total',
    colInvoice: 'Invoice', colRequest: 'Request', colInstalment: 'Instalment',
    colAmount: 'Amount', colDue: 'Due', colStatus: 'Status',
    markPaid: 'Mark paid', markUnpaid: 'Mark unpaid',
    chipPaid: 'Paid', chipUnpaid: 'Unpaid',
    agingOverdue: 'Overdue {n}d', agingDueIn: 'Due in {n}d', agingDue: 'Due {date}',
  },
  calendar: {
    title: 'Tour Calendar',
    subtitle: 'Live view of provisional and confirmed tours.',
    statusProvisional: 'Provisional', statusConfirmed: 'Confirmed', statusCompleted: 'Completed',
    addEvent: 'Add Event', addTeamEvent: 'Add Team Event', teamEvent: 'Team Event',
    addedBy: 'Added by {name}',
  },
  newTour: {
    title: 'New Tour Request',
    subtitle: 'Raise a request on behalf of a customer.',
    backToRequests: '← Back to requests',
    stepCustomer: 'Customer', stepTourDetails: 'Tour Details', stepGroupInfo: 'Group Info', stepReview: 'Review',
    brand: 'Brand', customer: 'Customer', tourDetails: 'Tour Details',
    whatsIncluded: "What's Included", groupInfo: 'Group Info',
    contactName: 'Contact name', departureDate: 'Departure Date', returnDate: 'Return Date',
    phone: 'Phone', createSelectCustomer: 'Create & select customer', creatingCustomer: 'Creating…',
    back: '← Back', continue: 'Continue →', stepOf: 'Step {n} of 4',
    creating: 'Creating…', createRequest: 'Create Request',
  },
  tour: {
    tabOverview: 'Overview',
    tabQuote: 'Quote',
    tabOps: 'Ops',
    tabItinerary: 'Itinerary',
    tabRooming: 'Rooming',
    tabFlights: 'Group Flights',
    tabTeams: 'Teams',
    tabRegisters: 'Registers',
    tabExpenses: 'Expenses',
    tabTeamInfo: 'Team Info',
    tabSocial: 'Social Media',
    tabLetters: 'Letters',
    tabResources: 'Resources',
    tabMessages: 'Messages',
    tabPreviews: 'Previews',
    breadcrumbDashboard: 'Dashboard',
    breadcrumbTours: 'Tours',
    secTourDetails: 'Tour Details', secGroupInfo: 'Group Info', secGroupType: 'Group Type',
    secContact: 'Contact', secItinerary: 'Itinerary',
  },
  // ── Football Operations portal (+ the read-only views it shares) ──────────
  footballOps: {
    portalName: 'Football Operations', badge: 'Football Ops', opsView: 'Ops view', signOut: 'Sign out',
    toursTitle: 'Tours', toursSub: 'Team info, fixtures, teams and itineraries for every booked tour.',
    upcoming: 'Upcoming & live', past: 'Past', allSports: 'All sports', searchTours: 'Search tours…',
    noToursTitle: 'No tours yet', noToursDesc: 'Booked tours will appear here. Contact MST if you think you should be seeing some.',
    noMatchTitle: 'No tours match', noMatchDesc: 'Try a different filter or search.',
    travellers: '{n} travellers', squadOne: '1 squad', squadMany: '{n} squads', pax: '{n} pax',
    allTours: 'All tours', tourNotFound: 'Tour not found.', loading: 'Loading…',
    tabOverview: 'Overview', tabFixtures: 'Fixtures', tabTeamInfo: 'Team Info', tabTeams: 'Teams',
    tabItinerary: 'Itinerary', tabSkeleton: 'Skeleton', tabNotes: 'Notes',
    secTourDetails: 'Tour Details', secFlights: 'Flights', secPeople: 'People', secGroupNotes: 'Notes from the group',
    fSport: 'Sport', fDestination: 'Destination', fHotel: 'Hotel', fDeparture: 'Departure', fReturn: 'Return',
    fGroupType: 'Group type', fTotalTravellers: 'Total travellers', fPlayers: 'Players', fSquads: 'Teams / Squads',
    fAirline: 'Airline', fOutboundFlight: 'Outbound flight', fOutboundDeparture: 'Outbound departure',
    fArrivalTime: 'Arrival time', fReturnFlight: 'Return flight', fReturnDeparture: 'Return departure',
    fReturnArrival: 'Return arrival', fGroupContact: 'Group contact', fPhone: 'Phone', fEmail: 'Email',
    fTourLeader: 'Tour leader', fTourLeaders: 'Tour leaders', fTotalGoalkeepers: 'Total goalkeepers',
    noTeamInfoTitle: 'No team info submitted yet', noTeamInfoDesc: 'It appears here once the coach fills in the team info form.',
    coachTeamInfo: 'Coach & Team Information',
    teamsNotSetTitle: 'Teams not set up yet', teamsNotSetDesc: 'Teams appear once the team info form is filled in or MST sets them up.',
    unplacedOne: '1 registered player is not in a team yet.', unplacedMany: '{n} registered players are not in a team yet.',
    playerOne: '1 player', playerMany: '{n} players', noPlayersPlaced: 'No players placed yet.',
    noAccess: "You don't have access to any part of this tour. Contact MST if you need it.",
  },
  portals: { uk_to_spain: 'UK to Spain', spain_to_uk: 'Spain to UK', padelante: 'Padelante' },
  squad: {
    squadN: 'Squad {n}', gender: 'Gender', yearsOfBirth: 'Year(s) of Birth', playersPerMatch: 'Players / Match',
    ability: 'Ability', homeKit: 'Home Kit', awayKit: 'Away Kit', jersey: 'Jersey', jersey2: 'Jersey 2',
    shorts: 'Shorts', socks: 'Socks', pattern: 'Pattern:', oneKitOnly: 'One kit only',
    preferredFixture: 'Preferred Fixture (Fixture Finder)', date: 'Date', time: 'Time', location: 'Location', ko: '{time} KO',
    copyFinder: 'Copy Fixture Finder', copied: 'Fixture finder text copied!', born: 'born {years}',
  },
  skeleton: {
    none: "No skeletal itinerary yet — MST staff haven't built it for this tour.",
    draft: 'Draft', pending_admin: 'Awaiting Admin Sign-Off', pending_group: 'Awaiting Group Sign-Off', approved: 'Fully Approved',
    slots: {
      arrival: 'Arrival', breakfast: 'Breakfast', morning1: 'Morning', morning2: 'Morning', lunch: 'Lunch',
      afternoon1: 'Afternoon', afternoon2: 'Afternoon', evening1: 'Evening', evening2: 'Evening',
      evmeal: 'Evening Meal', departure: 'Departure', activity: 'Activity',
    },
  },
  notes: {
    title: 'Notes', none: 'No notes yet.', tour: 'Tour', fromTour: 'From {label}', anotherTour: 'another tour',
    unknown: 'Unknown', justNow: 'Just now', earlier: 'Earlier notes', legacyHint: 'From the old notes box, before notes were dated',
  },
  fixtures: {
    title: 'Fixtures', countOne: '1 match · teams come from the Teams tab', countMany: '{n} matches · teams come from the Teams tab',
    noTeams: "No teams yet. They're created from the squads once the team info form is filled in, or can be added on the Teams tab.",
    noFixtures: 'No fixtures yet.', yourTeam: 'Your team', tourLeader: '· Tour leader: {name}',
    onItinerary: 'On the itinerary: {days}', dayN: 'Day {n}',
    notOnItinerary: 'Not on the itinerary yet. Add it from a Fixtures activity.',
    teamRemoved: 'Team removed', vs: ' vs ', oppTbc: 'Opposition TBC', oppCoach: 'Opp. coach', referee: 'Referee',
    home: 'Home', away: 'Away', oneKitOnly: '(one kit only)', matchOne: '1 match', matchMany: '{n} matches',
    shirt: '{c} shirt', shorts: '{c} shorts', socks: '{c} socks',
    clashAway: 'Opposition wear {c}, like our home shirt. Wear the away kit.',
    clashBoth: 'Opposition wear {c}, like both our kits. Bring bibs.',
    clashNoAway: 'Opposition wear {c}, like our shirt and there is no away kit. Bring bibs.',
  },
  itinerary: {
    needsDetails: 'Needs details',
    dayN: 'Day {n}', noActivities: 'No activities for this day.',
    noDates: 'No tour dates set — add departure and return dates first.',
    exportGroup: 'Export for Group', generating: 'Generating…',
    exportAll: 'Export full itinerary', exportFiltered: 'Export {name} only',
    filterSupplier: 'Supplier', allSuppliers: 'All suppliers', noSupplier: 'No supplier',
    showingN: 'Showing {n} of {total} activities', clearFilter: 'Clear',
    colTime: 'Time', colActivity: 'Activity', colLeaderNotes: 'Notes for tour leaders', colLocation: 'Location', colSupplier: 'Supplier', colStatus: 'Status', colTicket: 'Ticket',
    ticket: 'Ticket', question: '💬 Question', activity: 'Activity',
    status: {
      not_required: 'Auto Approved', pending: 'Pending', approved: 'Approved', overridden: 'Auto Approved',
      declined: 'Declined', question: 'Has a Question',
    },
    booking: { confirmed: 'Availability confirmed', declined: 'Availability declined', pending: 'Awaiting confirmation' },
    transfer: { bus: '🚌 Bus', tram: '🚊 Tram', walk: '🚶 Walk' },
    pickup: 'Pick-up', dropoff: 'Drop-off', supplierContact: 'Supplier contact', openMaps: 'Open in Google Maps',
    noDetails: 'No additional details available.', close: 'Close',
  },
  supplier: {
    portal: 'Supplier Portal', role: 'Supplier', pendingN: '{n} pending', signOut: 'Sign out', loading: 'Loading…',
    yourTours: 'Your Tours', noCurrent: 'No current or upcoming tours.',
    noTours: 'No tours assigned yet. MST will notify you when something needs your attention.', noToursShort: 'No tours assigned yet.',
    nothingComing: 'Nothing coming up. Tick "Show past tours" to see earlier ones.',
    countUpcomingOne: '1 current & upcoming tour · click a tour to view details and respond',
    countUpcomingMany: '{n} current & upcoming tours · click a tour to view details and respond',
    countAllOne: '1 tour · click a tour to view details and respond', countAllMany: '{n} tours · click a tour to view details and respond',
    showPast: 'Show past tours ({n})', activityOne: '1 activity', activityMany: '{n} activities',
    calendar: {
      listView: 'List', calendarView: 'Calendar', today: 'Today', prev: 'Previous month', next: 'Next month',
      more: '+{n} more', activityDay: 'Your activity', thisMonth: 'Tours this month', none: 'No tours this month.',
      legendUpcoming: 'Booked', legendAction: 'Needs your response', legendPast: 'Finished',
    },
    badge: { itinerary: 'Itinerary approval needed', pending: 'Awaiting confirmation', confirmed: 'Confirmed', declined: 'Declined' },
    docs: {
      title: 'Compliance Documents', desc: 'Insurance, H&S certs, hygiene certificates and other required documentation.',
      upload: 'Upload', name: 'Document name', namePh: 'e.g. Public Liability Insurance 2025', type: 'Document type', file: 'File',
      uploading: 'Uploading {n}%…', uploadBtn: 'Upload document', none: 'No documents uploaded yet.', view: 'View ↗',
      failed: 'Upload failed. Please try again.',
      types: { insurance: 'Insurance', health_safety: 'Health & Safety', hygiene_cert: 'Hygiene Certificate', pool_maintenance: 'Pool Maintenance', fire_escape: 'Fire Escape Plan', other: 'Other' },
    },
    tabs: { booking: 'Booking', tour: 'Tour Info', participants: 'Participants', itinerary: 'Itinerary', rooming: 'Rooming', food: 'Food', contractor: 'Contractor' },
    myTours: 'My Tours', unavailableTitle: "This tour isn't available",
    unavailableBody: "You're not booked on it or assigned to any of its activities. If you think that's wrong, please contact MST.",
    pax: '{n} pax', approvalRequired: 'Itinerary Approval Required', bookingRequired: 'Booking Confirmation Required',
    bookingTitle: 'Booking Confirmation', yourActivities: 'Your Activities', noActivities: 'No activities assigned to you on this tour yet.',
    fullDetails: 'Full details in Itinerary →', day: 'Day {n}', dayTbc: 'Day TBC', until: 'Until {time}',
    status: {
      pending: 'Awaiting your approval', not_sent: 'Not yet sent for approval', approved: 'Approved', overridden: 'Confirmed',
      not_required: 'Confirmed', declined: 'Declined', question: 'Question sent',
    },
    booking: {
      service: 'Service', confirmBooking: 'Confirm booking', decline: 'Decline', confirm: 'Confirm', cancel: 'Cancel', saving: 'Saving…',
      noteConfirm: 'Confirmation note (optional)', noteDecline: 'Reason for declining (optional)',
      phConfirm: 'e.g. Confirmed — ref: 12345', phDecline: 'e.g. Not available on those dates', yourNote: 'Your note: "{note}"',
      invoice: 'Invoice', viewInvoice: 'View invoice', replace: 'Replace', uploading: 'Uploading {n}%…', uploadInvoice: 'Upload invoice',
      invoiceFailed: 'Invoice upload failed. Please try again.',
      statuses: { pending: 'Awaiting confirmation', confirmed: 'Confirmed', declined: 'Declined' },
    },
    approve: {
      items: 'Items requiring your approval', approveAll: '✓ Approve all', decline: '✕ Decline', question: '? I have a question',
      whatQuestion: 'What is your question?', questionPh: 'Type your question here…', saving: 'Saving…', sending: 'Sending…',
      send: 'Send question', back: 'Back', failed: 'Failed to submit. Please try again.',
    },
    overview: {
      title: 'Tour Overview', sport: 'Sport', destination: 'Destination', departure: 'Departure', return: 'Return',
      groupType: 'Group type', totalTravellers: 'Total travellers', players: 'Players', male: 'Male', female: 'Female',
    },
    leaders: { one: 'Tour Leader', many: 'Tour Leaders', none: 'No tour leader assigned yet.', name: 'Name', email: 'Email', phone: 'Phone', leads: 'Leads' },
    teams: {
      info: 'Team Information', squads: 'Squads', goalkeepers: 'Goalkeepers', teams: 'Teams',
      noLinked: 'No teams are linked to your matches yet.', notSubmitted: 'Team information not yet submitted.',
      playersN: '{team} players · {n}', noPlayers: 'No players placed in this team yet.',
    },
    participants: {
      title: 'Participant Information', none: 'No participant details submitted yet.',
      countOne: '1 participant', countMany: '{n} participants', export: 'Export CSV',
      reqTitle: 'Dietary / Medical / Safety Requirements', reqCountOne: '1 participant with requirements', reqCountMany: '{n} participants with requirements',
      allergies: 'Allergies:', dietary: 'Dietary:', medical: 'Medical:', safety: 'Safety:', requirements: 'Requirements',
      personal: 'Personal', fullName: 'Full Name', dob: 'Date of Birth', dobShort: 'DOB: {d}', gender: 'Gender', nationality: 'Nationality',
      passport: 'Passport / Travel', passportNo: 'Passport No.', expiry: 'Expiry', countryOfIssue: 'Country of Issue',
      contact: 'Contact', phone: 'Phone', email: 'Email', guardian: 'Guardian / Parent', name: 'Name', participantN: 'Participant {n}',
      csv: {
        no: 'No.', fullName: 'Full Name', travellerType: 'Traveller Type', dob: 'Date of Birth', gender: 'Gender', nationality: 'Nationality',
        passportNo: 'Passport No.', passportExpiry: 'Passport Expiry', countryOfIssue: 'Country of Issue', allergies: 'Allergies',
        dietary: 'Dietary Requirements', medical: 'Medical Conditions', safety: 'Safety Info', contactPhone: 'Contact Phone',
        contactEmail: 'Contact Email', guardianName: 'Guardian Name', guardianPhone: 'Guardian Phone', guardianEmail: 'Guardian Email',
      },
    },
    itinerary: {
      yourActivitiesHint: "The activities you're the supplier for. You can upload a ticket PDF for each.", full: 'Full Itinerary',
      none: 'No itinerary items yet.', noneToDisplay: 'No items to display.', ticket: '↓ Ticket', replace: '↑ Replace',
      uploadTicket: '↑ Upload ticket', uploadFailed: 'Failed to upload ticket. Please try again.', removeConfirm: 'Remove this ticket?',
      removeFailed: 'Failed to remove ticket. Please try again.',
    },
    rooming: { title: 'Rooming', none: 'Rooming not yet assigned.', staff: 'MST staff', beds: '{n} beds', empty: 'Empty', room: 'Room {n}' },
    food: { sandwich: 'Sandwich Orders', tgb: 'TGB Orders', bela: 'Bela Café Orders', none: 'No orders submitted yet.', dateTbc: 'Date TBC', groupOf: 'Group of {n}' },
    confirm: {
      loading: 'Loading confirmation details…', invalidTitle: 'Link not valid', invalid: 'Invalid or expired link.',
      notFound: 'Itinerary item not found.', failed: 'Something went wrong. Please try again.',
      contactUs: 'If you believe this is an error, please contact MST Sports Tours directly.',
      requestTitle: 'Itinerary approval request',
      requestIntro: 'MST Sports Tours is requesting your availability for the following items on the {tour} tour ({dates}).',
      yourItems: 'Your items', approveAll: '✓ Approve all items', declineAll: '✕ Decline all items',
      usedTitle: 'Already responded', used: 'This approval link has already been used. Please contact MST Sports Tours if you need to change your response.',
      legacyTitle: 'Confirmation request', legacyIntro: 'MST Sports Tours is requesting confirmation of your availability for the following itinerary item.',
      tour: 'Tour', dates: 'Dates', activity: 'Activity', day: 'Day', time: 'Time', notes: 'Notes', confirm: '✓ Confirm', decline: '✕ Decline',
      submitting: 'Submitting your response…', approved: 'Approved', questionSent: 'Question sent', declined: 'Declined',
      approvedBody: 'Thank you — your availability has been confirmed. MST Sports Tours will be in touch with further details.',
      questionBody: 'Thank you — your question has been sent to the MST Sports Tours team. They will be in touch shortly.',
      declinedBody: 'Thank you for letting us know. MST Sports Tours will be in touch to discuss alternative arrangements.',
    },
    contractor: {
      updateTitle: 'Update Fixture / Session Details', titleField: 'Title / Activity', time: 'Time', notes: 'Notes (pitch, referee, opponent)',
      save: 'Save', cancel: 'Cancel', edit: 'Edit', saving: 'Saving…', saveFailed: 'Failed to save.',
      logTitle: 'Log Expense', description: 'Description', descPh: 'e.g. Referee fee', amount: 'Amount (€)', log: 'Log expense',
      saved: 'Saved', expenseFailed: 'Failed to save expense.',
    },
  },
};

export default en;
